/*
    HomePage.jsx
    新しいトップページ - 事業概要とCTAボタン
*/

import React from "react";
import { useCms, toLines } from "../cms/CmsProvider";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import NewsSection from "../components/molecules/NewsSection";
import { FaHandsHelping, FaClipboardList, FaHeart, FaChild } from "react-icons/fa";
import heroImage from '../assets/img/services.png';
import servicesImage1 from '../assets/img/services.png';
import servicesImage2 from '../assets/img/services2.jpg';
import servicesImage3 from '../assets/img/services3.jpg';

export default function HomePage() {
    const { t, text, image, items, site } = useCms("home");
    const formUrl = (field, fallback) => site[field] || fallback;

    // 事業紹介カード（microCMS の「事業1〜4」が空なら従来の内容）
    const defaultBusinesses = [
        { key: "childcare", img: servicesImage1, count: 3 },
        { key: "community", img: servicesImage2, count: 4 },
        { key: "research", img: servicesImage3, count: 3 },
    ].map(({ key, img, count }) => ({
        title: t(`homePage.business.${key}.title`),
        description: t(`homePage.business.${key}.description`),
        image: { url: img },
        items: Array.from({ length: count }, (_, i) => t(`homePage.business.${key}.item${i + 1}`)).join("\n"),
    }));
    // 画像が未登録のカードは、同じ順番の従来の画像を使う
    const businesses = items("businesses", defaultBusinesses).map((b, i) => ({
        ...b,
        image: b.image?.url ? b.image : defaultBusinesses[i]?.image,
    }));

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, duration: 0.6 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    return (
        <div className="home-page">
            {/* Hero Image */}
            <section className="home-hero-section">
                <motion.img
                    src={image("heroImage", heroImage)}
                    alt="子どもたち"
                    className="home-hero-image"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                />
            </section>

            {/* Tagline */}
            <motion.section
                className="home-tagline-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
            >
                <h1>{text("heroTitle", "homePage.hero.title")}</h1>
                <p>{text("heroSubtitle", "homePage.hero.subtitle")}</p>
            </motion.section>

            {/* Mission Statement */}
            <motion.section
                className="mission-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.3 }}
            >
                <motion.div className="mission-content" variants={itemVariants}>
                    <p className="mission-text">{text("missionText", "homePage.mission.text")}</p>
                </motion.div>
            </motion.section>

            {/* News */}
            <NewsSection />

            {/* CTA Buttons */}
            <motion.section
                className="cta-buttons-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.3 }}
            >
                <div className="cta-buttons-container">
                    <motion.div variants={itemVariants}>
                        <Link to="/programs" className="cta-card">
                            <div className="cta-icon"><FaChild /></div>
                            <h3>{text("ctaProgramsTitle", "homePage.cta.programs.title")}</h3>
                            <p>{text("ctaProgramsDesc", "homePage.cta.programs.description")}</p>
                        </Link>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                        <a href={formUrl("consultationFormUrl", "https://forms.gle/4sjhmEXvSSRFA5WG6")} target="_blank" rel="noopener noreferrer" className="cta-card">
                            <div className="cta-icon"><FaClipboardList /></div>
                            <h3>{text("ctaConsultationTitle", "homePage.cta.consultation.title")}</h3>
                            <p>{text("ctaConsultationDesc", "homePage.cta.consultation.description")}</p>
                        </a>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                        <a href={formUrl("volunteerFormUrl", "https://forms.gle/z8xSHYJ5PApRzgrSA")} target="_blank" rel="noopener noreferrer" className="cta-card">
                            <div className="cta-icon"><FaHandsHelping /></div>
                            <h3>{text("ctaVolunteerTitle", "homePage.cta.volunteer.title")}</h3>
                            <p>{text("ctaVolunteerDesc", "homePage.cta.volunteer.description")}</p>
                        </a>
                    </motion.div>
                </div>
            </motion.section>

            {/* Business Overview */}
            <motion.section
                className="business-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.2 }}
            >
                <motion.h2 variants={itemVariants}>{text("businessTitle", "homePage.business.title")}</motion.h2>

                <div className="business-cards">
                    {businesses.map((b, i) => (
                        <motion.div className="business-card" variants={itemVariants} key={i}>
                            {b.image?.url && (
                                <div className="business-card-image">
                                    <img src={b.image.url} alt={b.title} />
                                </div>
                            )}
                            <div className="business-card-content">
                                <h3>{b.title}</h3>
                                <p>{b.description}</p>
                                <ul>
                                    {toLines(b.items).map((item, j) => (
                                        <li key={j}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.section>

            {/* Donate CTA */}
            <motion.section
                className="donate-cta-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.3 }}
            >
                <motion.div className="donate-cta-content" variants={itemVariants}>
                    <FaHeart className="donate-cta-icon" />
                    <h2>{text("donateTitle", "homePage.donateCta.title")}</h2>
                    <p>{text("donateDescription", "homePage.donateCta.description")}</p>
                    <Link to="/donate" className="donate-cta-button">
                        {text("donateButton", "homePage.donateCta.button")}
                    </Link>
                </motion.div>
            </motion.section>
        </div>
    );
}
